import { Injectable } from '@angular/core';
import { AppEditionsMarketing } from '@galaxy/marketplace-apps/v1';
import { createEmptyValue } from '../diff-utils';
import {
  ChangedField,
  EditionMarketingDiffModel,
  EditionMarketingFieldDisplayType,
} from './edition-marketing-diff-model';

@Injectable()
export class EditionMarketingDiffService {
  private END_USER_MARKETING_FIELD = 'endUserMarketing';
  private RESELLER_MARKETING_FIELD = 'resellerMarketing';

  private fieldDisplayTypeMapping: Map<string, EditionMarketingFieldDisplayType> = new Map([
    ['usesResellerMarketing', EditionMarketingFieldDisplayType.Boolean],
    ['description', EditionMarketingFieldDisplayType.Text],
    ['keySellingPoints', EditionMarketingFieldDisplayType.KeySellingPoint],
  ]);

  /**
   * Calculates the differences between two records. Intended to be used with draft and
   * published versions of the same record.
   * @param draft A version of that has unpublished changes pending.
   * @param published The version of that is currently published to the store.
   * @returns {EditionMarketingDiffModel} An object containing several arrays with the categorized differences between the
   * two versions.
   */
  getDiffs(draft: AppEditionsMarketing, published: AppEditionsMarketing): EditionMarketingDiffModel {
    draft = draft || new AppEditionsMarketing();
    published = published || new AppEditionsMarketing();

    const diffModel = new EditionMarketingDiffModel();

    const allFields = [];
    for (const field in published) {
      if (Object.prototype.hasOwnProperty.call(published, field)) {
        allFields.push(field);
      }
    }
    for (const field in draft) {
      if (Object.prototype.hasOwnProperty.call(draft, field) && allFields.indexOf(field) < 0) {
        allFields.push(field);
      }
    }

    for (let i = 0; i < allFields.length; i++) {
      const field = allFields[i];
      if (Object.prototype.hasOwnProperty.call(published, field)) {
        if (Object.prototype.hasOwnProperty.call(draft, field)) {
          if (field === this.END_USER_MARKETING_FIELD) {
            diffModel.endUserMarketingChanges = this.diffObject(published[field], draft[field]);
          } else if (field === this.RESELLER_MARKETING_FIELD) {
            diffModel.resellerMarketingChanges = this.diffObject(published[field], draft[field]);
          } else {
            const change = this.diffField(published[field], draft[field], field);
            if (change) {
              diffModel.topLevelDataChanges.push(change);
            }
          }
        } else {
          // Field exists in published but not draft.
          if (field === this.END_USER_MARKETING_FIELD) {
            diffModel.endUserMarketingChanges = this.diffObject(published[field], null);
          } else if (field === this.RESELLER_MARKETING_FIELD) {
            diffModel.resellerMarketingChanges = this.diffObject(published[field], null);
          } else {
            const change = this.diffField(published[field], null, field);
            if (change) {
              diffModel.topLevelDataChanges.push(change);
            }
          }
        }
      } else {
        // Field exists in draft but not published.
        if (field === this.END_USER_MARKETING_FIELD) {
          diffModel.endUserMarketingChanges = this.diffObject(null, draft[field]);
        } else if (field === this.RESELLER_MARKETING_FIELD) {
          diffModel.resellerMarketingChanges = this.diffObject(null, draft[field]);
        } else {
          const change = this.diffField(null, draft[field], field);
          if (change) {
            diffModel.topLevelDataChanges.push(change);
          }
        }
      }
    }

    return diffModel;
  }

  private getDisplayType(fieldName: string): EditionMarketingFieldDisplayType {
    if (this.fieldDisplayTypeMapping.has(fieldName)) {
      return this.fieldDisplayTypeMapping.get(fieldName);
    }

    return EditionMarketingFieldDisplayType.Text;
  }

  private diffObject(oldObject: any, newObject: any): ChangedField[] {
    oldObject = oldObject || {};
    newObject = newObject || {};

    const changes = [];
    for (const field in oldObject) {
      if (Object.prototype.hasOwnProperty.call(oldObject, field)) {
        if (Object.prototype.hasOwnProperty.call(newObject, field)) {
          // If the object has a nested array check each field in it for changes.
          if (Array.isArray(oldObject[field])) {
            const oldField: Array<any> = oldObject[field];
            const newField: Array<any> = newObject[field];
            // Arrays should be considered equal if they are the same length and have the same items
            // in the same order.
            let length = 0;
            if (oldField.length === newField.length) {
              length = oldField.length;
            } else if (oldField.length > newField.length) {
              length = oldField.length;
            } else {
              length = newField.length;
            }
            for (let i = 0; i < length; i++) {
              // compare all values in the oldValues to all values in new values if it's array of
              // strings
              if (
                !(oldField[i] !== null && typeof oldField[i] === 'object') &&
                !(newField[i] !== null && typeof newField[i] === 'object')
              ) {
                if (newField[i]) {
                  if (!oldField.includes(newField[i])) {
                    let value = ' ';
                    if (oldField[i]) {
                      value = oldField[i];
                    }
                    changes.push(new ChangedField(field, this.getDisplayType(field), value, newField[i], 'A'));
                    continue;
                  }
                }
                if (oldField[i]) {
                  if (!newField.includes(oldField[i])) {
                    let value = ' ';
                    if (newField[i]) {
                      value = newField[i];
                    }
                    changes.push(new ChangedField(field, this.getDisplayType(field), oldField[i], value, 'D'));
                  }
                }
              } else {
                const change = this.diffField(oldField[i], newField[i], field);
                if (change) {
                  changes.push(change);
                }
              }
            }
          } else {
            const change = this.diffField(oldObject[field], newObject[field], field);
            if (change) {
              changes.push(change);
            }
          }
        } else {
          // Field exists on oldObject but not newObject.
          if (Array.isArray(oldObject[field])) {
            newObject[field] = [];
            for (let i = 0; i < oldObject[field].length; i++) {
              const change = this.diffField(oldObject[field][i], newObject[field][i], field);
              if (change) {
                changes.push(change);
              }
            }
          } else {
            const change = this.diffField(oldObject[field], '', field);
            if (change) {
              changes.push(change);
            }
          }
        }
      }
    }

    for (const field in newObject) {
      if (Object.prototype.hasOwnProperty.call(newObject, field)) {
        if (!Object.prototype.hasOwnProperty.call(oldObject, field)) {
          // Field exists on newObject but not oldObject.
          if (Array.isArray(newObject[field])) {
            oldObject[field] = [''];
          } else {
            oldObject[field] = '';
          }
          const change = this.diffField(oldObject[field], newObject[field], field);
          if (change) {
            changes.push(change);
          }
        }
      }
    }

    return changes;
  }

  private diffField(oldField: any, newField: any, fieldName: string): ChangedField {
    if (!oldField && !newField) {
      return null;
    }
    if (oldField && !newField) {
      return new ChangedField(fieldName, this.getDisplayType(fieldName), oldField, createEmptyValue(oldField), 'D');
    }
    if (newField && !oldField) {
      return new ChangedField(fieldName, this.getDisplayType(fieldName), createEmptyValue(newField), newField, 'A');
    }

    let areSame = true;
    let change = '';
    if (Array.isArray(oldField)) {
      // Arrays should be considered equal if they are the same length and have the same items in
      // the same order.
      let length = 0;
      if (oldField.length === newField.length) {
        length = oldField.length;
      } else if (oldField.length > newField.length) {
        length = oldField.length;
      } else {
        length = newField.length;
      }
      for (let i = 0; i < length && areSame; i++) {
        const arrayChange = this.diffField(oldField[i], newField[i], fieldName);
        if (arrayChange) {
          areSame = false;
          change = arrayChange.changeType;
        }
      }
    } else if (oldField !== null && typeof oldField === 'object') {
      // Objects should be considered not equal if their JSON representations are not the same.
      if (JSON.stringify(oldField) === JSON.stringify(newField)) {
        areSame = true;
      } else if (JSON.stringify(oldField) > JSON.stringify(newField)) {
        change = 'D';
        areSame = false;
      } else {
        change = 'A';
        areSame = false;
      }
      for (const field in oldField) {
        if (field !== 'toApiJson') {
          if (!Object.prototype.hasOwnProperty.call(newField, field)) {
            change = 'D';
            areSame = false;
            newField[field] = createEmptyValue(oldField[field]);
          }
        }
      }
      for (const field in newField) {
        if (!Object.prototype.hasOwnProperty.call(oldField, field)) {
          if (field !== 'toApiJson') {
            change = 'A';
            areSame = false;
            oldField[field] = createEmptyValue(newField[field]);
          }
        }
      }
    } else {
      if (oldField !== newField) {
        change = 'A';
        areSame = false;
      }
    }
    if (!areSame) {
      return new ChangedField(fieldName, this.getDisplayType(fieldName), oldField, newField, change);
    }

    return null;
  }
}
